/**
 * Edit operations on project files. Every edit, from the editor UI, a plugin
 * command or an agent over MCP, is a list of these ops. Applying a list is
 * atomic and returns the inverse list, which is what undo applies.
 *
 * A path walks into a file's JSON. A segment is an object key, an array
 * index, or `{ id }`, which picks the array element with that id. Id segments
 * keep paths valid when other objects are added or reordered.
 */

export type PathSegment = string | number | { id: string };

export type Op =
  /** Sets an object key or replaces an array element. */
  | { op: 'set'; file: string; path: PathSegment[]; value: unknown }
  /** Deletes an object key, or removes an array element. */
  | { op: 'remove'; file: string; path: PathSegment[] }
  /** Inserts into an array. The last segment is the index (the length appends). */
  | { op: 'insert'; file: string; path: PathSegment[]; value: unknown }
  | { op: 'createFile'; file: string; value: unknown }
  | { op: 'deleteFile'; file: string };

/** File contents by project path. A missing key means the file does not exist. */
export type FileMap = Map<string, unknown>;

export class OpError extends Error {
  constructor(message: string, readonly op?: Op) {
    super(message);
    this.name = 'OpError';
  }
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function describe(path: PathSegment[]): string {
  return '/' + path.map((s) => (typeof s === 'object' ? `{id:${s.id}}` : String(s))).join('/');
}

/** Resolves `{ id }` and numeric segments against an array, returning the index. */
function indexIn(arr: unknown[], seg: PathSegment, path: PathSegment[]): number {
  if (typeof seg === 'number') {
    if (!Number.isInteger(seg) || seg < 0) throw new OpError(`Bad index ${seg} in ${describe(path)}`);
    return seg;
  }
  if (typeof seg === 'object') {
    const i = arr.findIndex((x) => isPlainObject(x) && x.id === seg.id);
    if (i < 0) throw new OpError(`No object with id "${seg.id}" at ${describe(path)}`);
    return i;
  }
  throw new OpError(`Expected an index or {id} for an array in ${describe(path)}`);
}

/** Walks to the container holding the last segment. Returns it plus the concrete last key. */
function locate(root: unknown, path: PathSegment[]): { parent: unknown; key: string | number; concrete: PathSegment[] } {
  if (path.length === 0) throw new OpError('Empty path; use createFile or deleteFile to replace a whole file');
  let node = root;
  const concrete: PathSegment[] = [];
  for (let i = 0; i < path.length - 1; i++) {
    const seg = path[i]!;
    if (Array.isArray(node)) {
      const idx = indexIn(node, seg, path);
      if (idx >= node.length) throw new OpError(`Index ${idx} out of range at ${describe(path)}`);
      concrete.push(typeof seg === 'object' ? seg : idx);
      node = node[idx];
    } else if (isPlainObject(node)) {
      if (typeof seg !== 'string') throw new OpError(`Expected a key in ${describe(path)}`);
      if (!(seg in node)) throw new OpError(`No "${seg}" at ${describe(path)}`);
      concrete.push(seg);
      node = node[seg];
    } else {
      throw new OpError(`Cannot walk into a ${node === null ? 'null' : typeof node} at ${describe(path)}`);
    }
  }
  const last = path[path.length - 1]!;
  if (Array.isArray(node)) return { parent: node, key: indexIn(node, last, path), concrete };
  if (isPlainObject(node)) {
    if (typeof last !== 'string') throw new OpError(`Expected a key at the end of ${describe(path)}`);
    return { parent: node, key: last, concrete };
  }
  throw new OpError(`Cannot change a ${node === null ? 'null' : typeof node} at ${describe(path)}`);
}

function applyOne(files: FileMap, op: Op): Op {
  switch (op.op) {
    case 'createFile': {
      if (files.has(op.file)) throw new OpError(`${op.file} already exists`, op);
      files.set(op.file, structuredClone(op.value));
      return { op: 'deleteFile', file: op.file };
    }
    case 'deleteFile': {
      if (!files.has(op.file)) throw new OpError(`${op.file} does not exist`, op);
      const old = files.get(op.file);
      files.delete(op.file);
      return { op: 'createFile', file: op.file, value: old };
    }
  }
  if (!files.has(op.file)) throw new OpError(`${op.file} is not open`, op);
  const root = files.get(op.file);
  const { parent, key, concrete } = locate(root, op.path);
  const last = op.path[op.path.length - 1]!;
  switch (op.op) {
    case 'set': {
      if (Array.isArray(parent)) {
        const i = key as number;
        if (i >= parent.length) throw new OpError(`Index ${i} out of range; use insert to add`, op);
        const old = parent[i];
        parent[i] = structuredClone(op.value);
        return { op: 'set', file: op.file, path: [...concrete, i], value: old };
      }
      const obj = parent as Record<string, unknown>;
      const had = Object.prototype.hasOwnProperty.call(obj, key);
      const old = obj[key as string];
      obj[key as string] = structuredClone(op.value);
      return had
        ? { op: 'set', file: op.file, path: [...concrete, key], value: old }
        : { op: 'remove', file: op.file, path: [...concrete, key] };
    }
    case 'remove': {
      if (Array.isArray(parent)) {
        const i = key as number;
        if (i >= parent.length) throw new OpError(`Index ${i} out of range`, op);
        const [old] = parent.splice(i, 1);
        return { op: 'insert', file: op.file, path: [...concrete, i], value: old };
      }
      const obj = parent as Record<string, unknown>;
      if (!Object.prototype.hasOwnProperty.call(obj, key)) throw new OpError(`Nothing to remove at ${describe(op.path)}`, op);
      const old = obj[key as string];
      delete obj[key as string];
      return { op: 'set', file: op.file, path: [...concrete, key], value: old };
    }
    case 'insert': {
      if (!Array.isArray(parent)) throw new OpError(`insert needs an array at ${describe(op.path)}`, op);
      if (typeof last !== 'number') throw new OpError('insert needs an index as its last path segment', op);
      if (last > parent.length) throw new OpError(`Index ${last} is past the end of the array`, op);
      parent.splice(last, 0, structuredClone(op.value));
      return { op: 'remove', file: op.file, path: [...concrete, last] };
    }
  }
}

/**
 * Applies ops to `files` atomically: if any op fails, `files` is unchanged
 * and the error is thrown. Returns the inverse ops, in the order to apply them.
 */
export function applyOps(files: FileMap, ops: readonly Op[]): Op[] {
  // Work on copies of only the files this list touches.
  const touched = new Set(ops.map((o) => o.file));
  const work: FileMap = new Map();
  for (const f of touched) if (files.has(f)) work.set(f, structuredClone(files.get(f)));
  const inverse: Op[] = [];
  for (const op of ops) inverse.unshift(applyOne(work, op));
  for (const f of touched) {
    if (work.has(f)) files.set(f, work.get(f));
    else files.delete(f);
  }
  return inverse;
}

/** The files a list of ops touches. */
export function filesOf(ops: readonly Op[]): Set<string> {
  return new Set(ops.map((o) => o.file));
}
